/**
 * Operaciones — data-scope build health (Plan J, J6).
 *
 * One row per (project, scope): what the last build did, whether it was
 * cut from the project file that is live right now, how much disk it
 * holds and how long it held the tenant write lock.
 *
 * THREE RULES THIS PANEL IS BUILT AROUND
 * --------------------------------------
 * 1. **No state is signalled by colour alone.** Every status is a
 *    `StatusChip` — a glyph and a word — and so is the freshness
 *    marker. The product owner has red-green colour blindness and the
 *    band palette (teal / deep red) is the pair that survives it. The
 *    scheduler dot two cards up does NOT follow this rule; that is a
 *    separate finding, not a licence.
 * 2. **A backend identifier never reaches the screen.** `resolveStatus`
 *    maps the raw `scope_builds.status` through a dictionary and, on a
 *    miss, renders the raw key with a warning glyph and `unknown: true`
 *    rather than prettifying it. A status added to
 *    `ck_scope_builds_status` later shows up as a defect here instead
 *    of as a plausible-looking label.
 * 3. **The numbers carry their own caveat.** `measurement_gaps` is
 *    rendered as a callout, not dropped: a `failed` build records no
 *    `timings_ms`, so every lock-hold figure on this screen is a
 *    distribution over SUCCESSES. A panel that hid that would
 *    confidently understate the one number J1 left open.
 */

import { Card, CardBody } from "@omnitok/ui";
import {
  DataTable,
  EmptyState,
  Callout,
  StatusChip,
  resolveStatus,
  type Column,
  type StatusDef,
} from "@pervasivemind/admin-ui";
import { Layers } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useT } from "@/i18n";
import { parseAsUTC } from "@/lib/admin-ai-api";

import {
  scopeOpsApi,
  type ScopeOpsOverview,
  type ScopeOpsScope,
} from "./scope-ops-api";

/** One table row: a scope, plus the project it was cut for. */
interface Row extends ScopeOpsScope {
  projectSlug: string;
  projectName: string;
}

/** Bytes, binary units. `null` renders as an em dash, never as 0. */
function formatBytes(value: number | null): string {
  if (value === null) return "—";
  if (value < 1024) return `${value} B`;
  const units = ["KiB", "MiB", "GiB", "TiB"];
  let n = value / 1024;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(n >= 100 ? 0 : 1)} ${units[i]}`;
}

/** Milliseconds as seconds once they stop being readable as ms. */
function formatMs(value: number | null): string {
  if (value === null) return "—";
  if (value < 1000) return `${value} ms`;
  return `${(value / 1000).toFixed(1)} s`;
}

/** ``scope_builds`` stamps are naive UTC — never read them as local (#259). */
export function formatTs(value: string | null): string {
  if (value === null) return "—";
  const d = parseAsUTC(value);
  return d === null ? value : d.toLocaleString();
}

export function ScopesPanel(): JSX.Element | null {
  const { t } = useT();
  const [data, setData] = useState<ScopeOpsOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    try {
      setData(await scopeOpsApi.overview());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "error");
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Live refresh while a build is in flight, on the same 30 s cadence
  // the rest of the cockpit uses. Keyed on a `building` row rather than
  // on the page's `hasLiveJob`: a round also runs from the CLI and from
  // the rebuild endpoint, neither of which produces a loader job, and a
  // table frozen mid-round is the kind of stale screen this panel
  // exists to replace. The endpoint takes no lock, so polling it during
  // a round costs nothing the round can feel.
  const building = useMemo(
    () =>
      (data?.projects ?? []).some((project) =>
        project.scopes.some((scope) => scope.status === "building"),
      ),
    [data],
  );
  useEffect(() => {
    if (!building) return;
    const id = setInterval(() => void load(), 30_000);
    return () => clearInterval(id);
  }, [building, load]);

  /** Raw `scope_builds.status` -> label + tone. A miss is loud. */
  const statusDict = useMemo<Record<string, StatusDef>>(
    () => ({
      built: { label: t("operations.scopes.status.built"), tone: "success" },
      building: {
        label: t("operations.scopes.status.building"),
        tone: "running",
      },
      failed: { label: t("operations.scopes.status.failed"), tone: "danger" },
      no_mapping: {
        label: t("operations.scopes.status.noMapping"),
        tone: "warning",
      },
      not_applicable: {
        label: t("operations.scopes.status.notApplicable"),
        tone: "neutral",
      },
    }),
    [t],
  );

  /** The CLOSED ``measurement_gaps`` vocabulary, translated.
   *
   * Keyed on ``ops.MEASUREMENT_GAPS[].key`` and spelled with literal
   * ``t()`` calls so the i18n key test can see them. A key added on the
   * backend without a line here renders its raw English detail rather
   * than silently disappearing. */
  const gapLabels = useMemo<Record<string, string>>(
    () => ({
      no_timings_on_failed_builds: t("operations.scopes.gaps.noTimings"),
      no_end_timestamp_unless_built: t("operations.scopes.gaps.noEnd"),
      elapsed_ms_not_persisted: t("operations.scopes.gaps.noElapsed"),
    }),
    [t],
  );

  const rows = useMemo<Row[]>(
    () =>
      (data?.projects ?? []).flatMap((project) =>
        project.scopes.map((scope) => ({
          ...scope,
          projectSlug: project.project_slug,
          projectName: project.project_name,
        })),
      ),
    [data],
  );

  const unmeasuredProjects = useMemo(
    () =>
      (data?.projects ?? [])
        .filter((p) => p.bytes_on_disk === null)
        .map((p) => p.project_name),
    [data],
  );

  const columns = useMemo<Column<Row>[]>(
    () => [
      {
        id: "project",
        header: t("operations.scopes.col.project"),
        cell: (row) => (
          <span className="font-medium text-neutral-900">
            {row.projectName}
          </span>
        ),
      },
      {
        id: "scope",
        header: t("operations.scopes.col.scope"),
        cell: (row) => (
          <span>
            <span className="font-medium text-neutral-900">
              {row.scope_name ?? row.scope_id}
            </span>
            <span className="ml-2 font-mono text-xs text-neutral-500">
              {row.scope_id}
            </span>
          </span>
        ),
      },
      {
        id: "status",
        header: t("operations.scopes.col.status"),
        cell: (row) => {
          // `null` is NOT a missing label — it is the state "this pair
          // has never built", whose readers get a refusal rather than
          // stale numbers. Rendering it through `resolveStatus` would
          // produce an em dash and lose the fact.
          if (row.status === null) {
            return (
              <StatusChip
                label={t("operations.scopes.status.neverBuilt")}
                tone="warning"
              />
            );
          }
          if (row.stuck) {
            return (
              <StatusChip
                label={t("operations.scopes.status.stuck")}
                tone="danger"
                title={t("operations.scopes.stuckHint")}
              />
            );
          }
          const resolved = resolveStatus(
            row.status,
            statusDict,
            t("operations.scopes.unknownStatus"),
          );
          return (
            <StatusChip
              label={resolved.label}
              tone={resolved.tone}
              unknown={resolved.unknown}
              title={resolved.title ?? row.error_kind ?? undefined}
            />
          );
        },
      },
      {
        id: "freshness",
        header: t("operations.scopes.col.freshness"),
        headerTitle: t("operations.scopes.freshnessHint"),
        cell: (row) => {
          if (row.data_version === null || row.project_data_version === null) {
            return <span className="text-neutral-500">—</span>;
          }
          const current = row.data_version === row.project_data_version;
          return (
            <StatusChip
              label={
                current
                  ? t("operations.scopes.fresh")
                  : t("operations.scopes.stale")
              }
              tone={current ? "success" : "warning"}
              title={`${row.data_version} / ${row.project_data_version}`}
            />
          );
        },
      },
      {
        id: "rows",
        header: t("operations.scopes.col.rows"),
        align: "right",
        cell: (row) =>
          row.row_count === null ? "—" : row.row_count.toLocaleString(),
      },
      {
        id: "size",
        header: t("operations.scopes.col.size"),
        align: "right",
        cell: (row) =>
          row.bytes_error !== null ? (
            <span className="text-neutral-500" title={row.bytes_error}>
              {t("operations.scopes.notMeasured")}
            </span>
          ) : (
            formatBytes(row.bytes_on_disk)
          ),
      },
      {
        id: "lock",
        header: t("operations.scopes.col.lock"),
        headerTitle: t("operations.scopes.lockHint"),
        align: "right",
        cell: (row) =>
          row.timings_missing ? (
            <span
              className="text-neutral-500"
              title={t("operations.scopes.gaps.noTimings")}
            >
              {t("operations.scopes.notMeasured")}
            </span>
          ) : (
            <span>
              {formatMs(row.lock_hold_ms)}
              {row.gap_pct !== null && (
                <span className="ml-1 text-xs text-neutral-500">
                  ({t("operations.scopes.gap", { pct: row.gap_pct })})
                </span>
              )}
            </span>
          ),
      },
      {
        id: "when",
        header: t("operations.scopes.col.when"),
        cell: (row) => (
          <span className="text-neutral-600">
            {formatTs(row.built_at ?? row.started_at)}
          </span>
        ),
      },
    ],
    [statusDict, t],
  );

  // Nothing to say before the first response, and nothing to say on a
  // tenant with no scope at all: the panel appears when it has content.
  if (!loaded) return null;
  if (error !== null) {
    return (
      <Card variant="bordered">
        <CardBody>
          <Callout tone="danger" title={t("operations.scopes.title")}>
            {error}
          </Callout>
        </CardBody>
      </Card>
    );
  }
  if (data === null || data.scope_count === 0) return null;

  return (
    <Card variant="bordered" noPadding>
      <header className="border-b border-neutral-200 bg-neutral-50 px-4 py-2">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="text-sm font-semibold text-neutral-900">
            {t("operations.scopes.title")}
          </span>
          <span className="text-xs text-neutral-500">
            {t("operations.scopes.summary", {
              scopes: rows.length,
              projects: data.projects.length,
              size: formatBytes(data.totals.bytes_on_disk),
              free: formatBytes(data.totals.free_bytes),
            })}
          </span>
          {data.totals.stuck > 0 && (
            <StatusChip
              label={t("operations.scopes.stuckCount", {
                count: data.totals.stuck,
              })}
              tone="danger"
            />
          )}
          {data.totals.failed > 0 && (
            <StatusChip
              label={t("operations.scopes.failedCount", {
                count: data.totals.failed,
              })}
              tone="danger"
            />
          )}
          {unmeasuredProjects.length > 0 && (
            <StatusChip
              label={t("operations.scopes.sizeIncomplete", {
                projects: unmeasuredProjects.join(", "),
              })}
              tone="warning"
            />
          )}
        </div>
      </header>
      <div className="px-4 py-3">
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => `${row.projectSlug}:${row.scope_id}`}
          caption={t("operations.scopes.title")}
          empty={
            <EmptyState
              icon={<Layers size={22} />}
              title={t("operations.scopes.empty.title")}
              description={t("operations.scopes.empty.body")}
            />
          }
        />
        {data.orphan_effective.length > 0 && (
          <Callout
            tone="warning"
            title={t("operations.scopes.orphans.title")}
            className="mt-4"
          >
            <p>{t("operations.scopes.orphans.body")}</p>
            <ul className="mt-2 space-y-0.5">
              {data.orphan_effective.map((orphan) => (
                <li key={orphan.scope} className="font-mono text-xs">
                  {orphan.scope} — {formatBytes(orphan.bytes_on_disk)}
                </li>
              ))}
            </ul>
          </Callout>
        )}
        <Callout
          tone="info"
          title={t("operations.scopes.gaps.title")}
          className="mt-4"
        >
          <p>{t("operations.scopes.gaps.body")}</p>
          <ul className="mt-2 list-disc space-y-0.5 pl-4">
            {data.measurement_gaps.map((gap) => {
              // The backend's ``detail`` is English prose, so the screen
              // renders a translated line keyed on the CLOSED ``key``
              // vocabulary instead. A key with no entry is shown as the
              // raw English, monospaced and titled — the same posture
              // ``resolveStatus`` takes: a missing label is a defect to
              // see, never a plausible-looking invention.
              const label = gapLabels[gap.key];
              return (
                <li key={gap.key}>
                  {label ?? (
                    <span
                      className="font-mono text-xs"
                      title={t("operations.scopes.gaps.untranslated")}
                    >
                      {gap.detail}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </Callout>
      </div>
    </Card>
  );
}
